import { ArrowLeftIcon, EllipsisHorizontalIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Alert } from '@heroui/alert';
import { Button } from '@heroui/button';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@heroui/dropdown';
import { Tab, Tabs } from '@heroui/tabs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { useAttioControllerSyncSite } from '@/api/generated/attio/attio';
import type { BillDto } from '@/api/generated/schemas';
import { DeleteBillModal } from '@/components/bills';
import { Icon } from '@/components/common';
import { SiteContractsCard } from '@/components/contracts';
import {
  AccountManagementStatsCards,
  ActivityCard,
  ContractSummaryCard,
  DataRequestCard,
  DeleteSiteModal,
  EditSiteModal,
  RaiseDisputeModal,
  SiteBillingDetailsCard,
  SiteBillsTable,
  SiteDetailsCard,
  SiteFeaturesCard,
  SiteHeader,
  SiteIssuesPanel,
  SiteListsCard,
  SiteMsatsCard,
  SitePeopleCard,
  SiteStatsCards,
  SiteUsageSplitCard,
  SiteVasSection,
  SyncStatusCard,
} from '@/components/sites';
import {
  useAccountManagementServiceLinksSubscription,
  useDisputesSubscription,
  useEmailsSubscription,
  useNotesSubscription,
  useSiteSubscription,
} from '@/hooks/useFirestoreSubscriptions';
import { useReportAutoDownload } from '@/hooks/useReportAutoDownload';
import { useBillsBySite } from '@/queries/bills.queries';
import { useSite } from '@/queries/sites.queries';

const siteDetailSearchSchema = z.object({
  tab: z.enum(['overview', 'account-management', 'analysis']).optional().default('overview'),
  /** Customer report to download on arrival — the target of the Attio task link raised for the EM */
  report: z.string().optional(),
  /** Pre-filter the Activity feed, used by the open disputes banner */
  activity: z.enum(['dispute']).optional(),
});

export const Route = createFileRoute('/sites/$siteId')({
  component: SiteDetail,
  validateSearch: siteDetailSearchSchema,
});

function SiteDetail() {
  const { siteId } = Route.useParams();
  const navigate = useNavigate();
  const { tab, report, activity } = Route.useSearch();
  useReportAutoDownload(siteId, report);
  const { data: site, isLoading } = useSite(siteId);
  const { data: bills = [] } = useBillsBySite(siteId);
  const annualUsage = site?.annualUsageAnalysis;

  const syncSiteMutation = useAttioControllerSyncSite();

  // Subscribe to real-time updates (e.g., from CRM webhook sync and account management webhooks)
  useSiteSubscription(siteId);
  useAccountManagementServiceLinksSubscription(siteId);
  useNotesSubscription(siteId);
  useEmailsSubscription(siteId);
  useDisputesSubscription(siteId);

  // Delete bill modal state
  const [billToDelete, setBillToDelete] = useState<BillDto | null>(null);
  const [showRaiseDispute, setShowRaiseDispute] = useState(false);
  const [isEditSiteOpen, setIsEditSiteOpen] = useState(false);
  const [isDeleteSiteOpen, setIsDeleteSiteOpen] = useState(false);

  // Check if any bills have billing period warnings
  const hasAnyBillingPeriodWarnings = bills.some((bill) => bill.hasBillingPeriodWarning);

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading site details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-gray-500">Site not found</p>
            <Button
              variant="light"
              startContent={<Icon as={ArrowLeftIcon} size="sm" color="inherit" />}
              className="mt-4"
              onPress={() => navigate({ to: '..' })}
            >
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Warning Banner for Long Billing Periods */}
        {hasAnyBillingPeriodWarnings && (
          <Alert
            color="warning"
            title="Long Billing Periods Detected"
            description="This site has one or more bills with long billing periods (over 100 days). Please review the bills below for accuracy."
            icon={<Icon as={ExclamationTriangleIcon} size="md" />}
          />
        )}

        {/* Header */}
        <SiteHeader site={site} onBack={() => navigate({ to: '..' })} onEditSite={() => setIsEditSiteOpen(true)} />

        {/* Tabs with inline action buttons */}
        <div className="relative">
          {/* Action buttons positioned at tab level */}
          <div className="absolute top-2 right-0 z-10 flex gap-2">
            <Button
              variant="flat"
              color="warning"
              size="sm"
              className="border border-warning-300 text-warning-700 hover:bg-warning-200"
              startContent={<Icon as={ExclamationTriangleIcon} size="sm" color="inherit" />}
              onPress={() => setShowRaiseDispute(true)}
            >
              Raise Dispute
            </Button>
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button variant="bordered" size="sm" isIconOnly aria-label="More actions">
                  <Icon as={EllipsisHorizontalIcon} size="md" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Site actions"
                onAction={(key) => {
                  if (key === 'edit') setIsEditSiteOpen(true);
                  if (key === 'delete') setIsDeleteSiteOpen(true);
                }}
              >
                <DropdownSection showDivider>
                  <DropdownItem key="edit">Edit Site Details</DropdownItem>
                  <DropdownItem key="export">Export Data</DropdownItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownItem key="delete" color="danger" className="text-danger">
                    Delete Site
                  </DropdownItem>
                </DropdownSection>
              </DropdownMenu>
            </Dropdown>
          </div>

          <Tabs
            selectedKey={tab}
            onSelectionChange={(key) =>
              navigate({
                to: '/sites/$siteId',
                params: { siteId },
                search: { tab: key as 'overview' | 'account-management' | 'analysis' },
                replace: true,
              })
            }
            aria-label="Site information tabs"
            className="w-full"
          >
            <Tab key="overview" title="Overview">
              <div className="space-y-6 pt-4">
                {/* Site Details + Billing - Two Column Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <SiteDetailsCard site={site} />
                  <SiteBillingDetailsCard site={site} />
                </div>

                {/* Contract positions on both axes, each with what it was signed to earn */}
                <ContractSummaryCard
                  contractSummary={site.contractSummary}
                  meteringContractSummary={site.meteringContractSummary}
                />

                {/* Contracts Section */}
                <SiteContractsCard siteId={siteId} />

                {/* People — includes Primary + Billing contact slots and the custom roster */}
                <SitePeopleCard site={site} />

                {/* Site Lists Card */}
                <SiteListsCard siteId={siteId} />

                {/* Sync Status */}
                <SyncStatusCard
                  attioSync={site.attioSync}
                  ryoContractsSync={site.ryoContractsSync}
                  onSyncToAttio={() => syncSiteMutation.mutate({ siteId })}
                  isSyncingToAttio={syncSiteMutation.isPending}
                />
              </div>
            </Tab>

            <Tab key="account-management" title="Account Management">
              <div className="space-y-6 pt-4">
                {/* Issues needing attention: open disputes */}
                <SiteIssuesPanel
                  site={site}
                  onViewDisputes={() =>
                    navigate({
                      to: '/sites/$siteId',
                      params: { siteId },
                      search: { tab: 'account-management', activity: 'dispute' },
                      replace: true,
                    })
                  }
                />

                {/* VAS position for the current metering contract (PD-507), shown in every state */}
                <SiteVasSection site={site} />

                {/* Account Management Stats */}
                <AccountManagementStatsCards site={site} />

                {/* Data Request + MSATS + Site Features - Three Column Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                  <DataRequestCard site={site} />
                  <SiteMsatsCard site={site} />
                  <SiteFeaturesCard site={site} />
                </div>

                {/* Activity (notes + emails + service records) */}
                <ActivityCard site={site} initialFilter={activity} />
              </div>
            </Tab>

            <Tab key="analysis" title="Analysis">
              <div className="space-y-6 pt-4">
                {/* Stats Cards */}
                <SiteStatsCards
                  annualUsage={annualUsage}
                  annualBill={site.annualBill}
                  billedSpend={site.billedSpend}
                  ryoBilledSpend={site.ryoBilledSpend}
                  currentRevenue={site.currentRevenue}
                />

                {/* Usage Split Card */}
                <SiteUsageSplitCard
                  annualUsage={annualUsage}
                  lastUpdated={site.annualUsageAnalysisUpdatedAt}
                  ryoUsage={site.ryoUsage}
                />

                {/* Bills Section */}
                <SiteBillsTable bills={bills} onDeleteBill={setBillToDelete} />
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* Delete Bill Modal */}
        {billToDelete && (
          <DeleteBillModal
            isOpen={!!billToDelete}
            onClose={() => setBillToDelete(null)}
            bill={billToDelete}
            linkedSites={site ? [site] : []}
          />
        )}

        <EditSiteModal isOpen={isEditSiteOpen} onClose={() => setIsEditSiteOpen(false)} site={site} />
        {isDeleteSiteOpen && (
          <DeleteSiteModal
            isOpen={isDeleteSiteOpen}
            onClose={() => setIsDeleteSiteOpen(false)}
            site={site}
            onDeleted={() => navigate({ to: '/sites' })}
          />
        )}

        {/* Raise Dispute Modal (page-level action) */}
        {showRaiseDispute && (
          <RaiseDisputeModal isOpen={showRaiseDispute} onClose={() => setShowRaiseDispute(false)} site={site} />
        )}
      </div>
    </div>
  );
}
